import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import _, { identity } from 'lodash'
import { Trans, useTranslation } from 'react-i18next'

import styles from '../ReassignDonationModal.module.scss'

import { ReassignDonationWithMarkedModalProps } from './ReassignDonationWithMarkedModal.types'
import { useMutation, useQuery, useQueryList } from '@percent/admin-dashboard/common/hooks'
import {
  AcknowledgeModal,
  ActionModal,
  AsyncSelect,
  ConfirmationModal,
  FormField,
  Modal,
  Select,
  Text,
  TextInput,
  SelectOption
} from '@percent/lemonade'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import countries from '@percent/admin-dashboard/i18n/data/countries'
import { createShortLink } from '@percent/utility'
import i18n from '@percent/admin-dashboard/i18n/config'
import { Organisation } from '@percent/admin-dashboard/api/types'
import { ReassignmentKind } from '@percent/admin-dashboard/api/actions/donations/donations.types'
import { isApiError } from '@percent/admin-dashboard/common/utility/isApiError'
import { isStringDefined } from '@percent/admin-dashboard/common/utility/isDefined'

const THROTTLING_TIME = 500

const reasons = {
  [ReassignmentKind.Unpayable]: [
    'Undeliverable - bank transfer fail',
    'Undeliverable - card payment fail',
    'Undeliverable - cheque returned',
    'Undeliverable - compliance fail',
    'Organisation was set incorrectly'
  ],
  other: 'Other'
}

const createOptions = (reassignmentKind: ReassignmentKind.Unpayable) => {
  const allReasons = [...(reasons[reassignmentKind] || []), reasons.other]

  return allReasons.map(reason => ({
    label: reason,
    value: reason
  }))
}

function requiredConditionally({
  schema,
  condition,
  message
}: {
  schema: Yup.StringSchema<string | null | undefined>
  condition: boolean
  message: string
}) {
  return condition ? schema.required(message) : schema
}

export function ReassignDonationWithMarkedModal({
  open,
  onClose,
  refresh,
  sourceOrganisationName,
  selectedDonation
}: ReassignDonationWithMarkedModalProps) {
  const { t } = useTranslation()
  const { adminService, donationsService } = useServices()
  const [query, setQuery] = useState<string>('')
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const pendingReassignment = selectedDonation?.pendingReassignment
  const options = createOptions(ReassignmentKind.Unpayable)
  const shortLanguage = i18n.language.split('-')[0]

  const [successDialogState, setSuccessDialogState] = useState(false)
  const [errorDialogState, setErrorDialogState] = useState(false)

  const [selectedReasonOption, setSelectedReasonOption] = useState<SelectOption | undefined>(
    options.find(option => option.value === pendingReassignment?.reason)
  )

  const [{ data: defaultOrganisation, isLoading: isDefaultOrganisationLoading }] = useQuery(
    adminService.getOrganisation,
    {
      id: pendingReassignment?.newOrganisationId ?? ''
    },
    { skip: !pendingReassignment?.newOrganisationId }
  )

  const [selectedOrganisationName, setSelectedOrganisationName] = useState<string | null | undefined>(
    defaultOrganisation?.name ?? pendingReassignment?.newOrganisationId ?? selectedDonation?.organisation?.name
  )

  useEffect(() => {
    if (!isDefaultOrganisationLoading) {
      setSelectedOrganisationName(defaultOrganisation?.name ?? selectedDonation?.organisation?.name)
    }
  }, [isDefaultOrganisationLoading, defaultOrganisation, selectedDonation])

  const [{ isLoading, error }, { apiFunc: reassignMarkedApiFunc }] = useMutation(
    donationsService.reassignMarkedDonation,
    () => setSuccessDialogState(true),
    () => setErrorDialogState(true)
  )

  const [{ dataOrNull: organisationsData, isLoading: isOrganisationsLoading }, { query: organisationsQuery }] =
    useQueryList(adminService.getOrganisations, {
      pageSize: 25
    })

  const formik = useFormik({
    validateOnMount: true,
    enableReinitialize: true,
    initialValues: {
      reassignmentKind: pendingReassignment?.kind ?? '',
      reassignedReason: pendingReassignment?.reason ?? '',
      customReason: '',
      organisationId: defaultOrganisation?.id ?? ''
    },
    validationSchema: () =>
      Yup.object().shape({
        reassignedReason: Yup.string().oneOf(Object.values(reasons).flat()).required(t('errorMessage.required')),
        organisationId: requiredConditionally({
          schema: Yup.string().trim(),
          condition: false,
          message: t('errorMessage.required')
        }),
        customReason: Yup.string()
          .nullable()
          .optional()
          .when('reassignedReason', {
            is: 'Other',
            then: Yup.string()
              .required(t('reassignment.customReason.required'))
              .trim()
              .min(1, t('errorMessage.orgFieldCharacterLimit', { fieldName: 'Custom reason' }))
              .max(255, t('errorMessage.orgFieldCharacterLimit', { fieldName: 'Custom reason' }))
          })
      }),
    onSubmit: async ({ reassignedReason, organisationId, customReason, reassignmentKind }) => {
      const reason = reassignedReason === reasons.other ? customReason : reassignedReason

      await reassignMarkedApiFunc({
        donationId: selectedDonation?.id ?? '',
        payload: {
          reason: isStringDefined(reason) ? reason : undefined,
          reassignmentKind: reassignmentKind as ReassignmentKind,
          newOrganisationId: isStringDefined(organisationId) ? organisationId : undefined
        }
      })
    }
  })

  const {
    touched,
    errors,
    setFieldTouched,
    setFieldValue,
    handleChange,
    handleBlur,
    setValues,
    handleSubmit,
    isValid,
    resetForm,
    values
  } = formik

  const handleCloseModal = () => {
    setShowConfirmationModal(false)
    onClose()
    refresh()
    resetForm()
    setSelectedOrganisationName(null)
    setSuccessDialogState(false)
  }

  const handleOnErrorClose = () => {
    setErrorDialogState(false)
  }

  const getOrganisationDescription = useCallback(
    (organisation: Organisation): string => {
      return [
        organisation.website ? createShortLink(organisation.website) : undefined,
        `${t('typography.id')}: ${organisation.registryId}`,
        countries.getName(organisation.countryCode, shortLanguage)
      ]
        .filter(identity)
        .join(' | ')
    },
    [shortLanguage, t]
  )

  const searchResults: SelectOption[] = useMemo(() => {
    const organisations = [...(organisationsData ?? []), defaultOrganisation].filter(o => !!o)

    return organisations
      ? organisations.map(organisation => ({
          value: organisation.id,
          label: organisation.name,
          description: getOrganisationDescription(organisation)
        }))
      : []
  }, [organisationsData, defaultOrganisation, getOrganisationDescription])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedOrganisationsQuery = useCallback(
    _.debounce<any>((filter: string) => {
      organisationsQuery({ query: filter })
    }, THROTTLING_TIME),
    []
  )
  useEffect(() => {
    if (query) {
      debouncedOrganisationsQuery(query)
    }
  }, [query, debouncedOrganisationsQuery])

  const successOrErrorModal =
    successDialogState && !errorDialogState ? (
      <AcknowledgeModal
        result="positive"
        title={t('dialog.reassignDonation.success.title')}
        description={
          <Trans
            i18nKey="dialog.reassignDonation.success.description"
            values={{ orgName: selectedOrganisationName }}
            components={{ b: <strong /> }}
          />
        }
        buttonText={t('button.close')}
        handleClose={handleCloseModal}
        viewTestId="success-donation-reassign-modal"
      />
    ) : (
      <AcknowledgeModal
        result="negative"
        title={t('dialog.reassignDonation.error.title')}
        buttonText={t('button.close')}
        handleClose={handleOnErrorClose}
        viewTestId="error-donation-reassign-modal"
      >
        <div className={styles.errorPage}>
          <Text size="medium">
            <Trans
              i18nKey="dialog.reassignDonation.error.description"
              values={{ orgName: selectedOrganisationName }}
              components={{ b: <strong /> }}
            />
          </Text>
          {error && isApiError(error) && (
            <div className={styles.errorDetails}>
              <Text size="small">{error.message}</Text>
              {error.reasons && (
                <ul>
                  {error.reasons.map((reason, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <li key={`reason-${i}`}>
                      <Text size="small">{reason}</Text>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </AcknowledgeModal>
    )

  return (
    <Modal open={open} onClose={handleCloseModal} aria-labelledby="reassign-marked-donations-form-modal">
      {!successDialogState && !errorDialogState ? (
        showConfirmationModal ? (
          <ConfirmationModal
            title={t('dialog.reassignDonation.confirmation.title')}
            loading={isLoading}
            primaryButtonText={t('button.reassign')}
            primaryBtnTestId="confirm-donation-reassignment"
            secondaryButtonText={t('button.cancel')}
            handleSubmit={handleSubmit}
            handleClose={() => setShowConfirmationModal(false)}
          >
            <Trans
              i18nKey="dialog.reassignDonation.confirmation.description"
              values={{ orgA: sourceOrganisationName, orgB: selectedOrganisationName }}
              components={{ b: <strong /> }}
            />
          </ConfirmationModal>
        ) : (
          <ActionModal
            title={t('dialog.reassignDonation.title')}
            primaryButtonText={t('button.reassign')}
            secondaryButtonText={t('button.cancel')}
            type="submit"
            variant={isValid ? 'primary' : 'secondary'}
            disabled={!(isValid && !isLoading)}
            loading={isLoading}
            handleClose={handleCloseModal}
            handleSubmit={() => setShowConfirmationModal(true)}
            primaryBtnTestId="reassign-donation-submit-button"
            secondaryBtnTestId="reassign-donation-cancel-button"
          >
            <div className={styles.reassignmentKind}>
              <Text size="small" fontWeight="bold" dataTestId="typeOfReassignmentLabel">
                {t('dialog.reassignDonation.typeOfReassignment.alert.title')}:{' '}
              </Text>
              <Text size="small" dataTestId="typeOfReassignmentValue">
                {t('typography.reassignmentKind.unpayable')}
              </Text>
            </div>
            <form>
              <div className={styles.withFieldGap}>
                <FormField label={t('typography.newOrganisation')} data-testid="organisationId" necessity="optional">
                  <AsyncSelect
                    name="organisationId"
                    placeholder={t('typography.search.organisation')}
                    onChange={e => {
                      setValues({
                        ...values,
                        organisationId: e?.value || ''
                      })
                      setSelectedOrganisationName(e?.label)
                    }}
                    options={searchResults}
                    setQuery={e => {
                      setQuery(e)
                      setFieldTouched('organisationId')
                    }}
                    query={query}
                    loading={isOrganisationsLoading || isDefaultOrganisationLoading}
                    loadingText="loading"
                    noResultsFoundText={t('errorMessage.noResult')}
                    data-testid="organisationSearch"
                    defaultValue={searchResults.find(option => option.value === values.organisationId)}
                  />
                </FormField>
              </div>
              <div className={styles.withFieldGap}>
                <FormField
                  label={t('dialog.reassignDonation.reassignedReason')}
                  data-testid="reassignedReason"
                  necessity="required"
                >
                  <Select
                    placeholder={t('dialog.reassignDonation.reassignedReason')}
                    options={options}
                    onChange={({ value }) => {
                      setFieldValue('reassignedReason', value)
                      setSelectedReasonOption(options.find(option => option.value === value))
                    }}
                    searchable={false}
                    defaultValue={selectedReasonOption}
                  />
                </FormField>
              </div>
              {values.reassignedReason === reasons.other && (
                <FormField
                  label={t('dialog.reassignDonation.customReason')}
                  necessity="required"
                  status={touched.customReason && errors.customReason ? 'danger' : 'default'}
                  statusMessage={errors.customReason || ''}
                  data-testid="customReason"
                >
                  <TextInput
                    name="customReason"
                    value={values.customReason || ''}
                    placeholder={t('dialog.reassignDonation.customReason')}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                </FormField>
              )}
            </form>
          </ActionModal>
        )
      ) : (
        successOrErrorModal
      )}
    </Modal>
  )
}
